import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { usePredictions } from "./usePredictions";
import { useEffect, useRef } from "react";
import { toast } from "./use-toast";

interface Notification {
  id: string;
  user_id: string;
  title: string;
  message: string;
  notification_type: string;
  prediction_id: string | null;
  is_read: boolean;
  criteria_matched: Record<string, unknown> | null;
  created_at: string;
}

interface NotificationPreferences {
  id: string;
  user_id: string;
  is_enabled: boolean;
  min_confidence: number;
  min_odds: number;
  max_odds: number;
  enabled_markets: string[];
  enabled_leagues: string[];
}

export function useNotifications() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["notifications", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from("notifications")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data || []) as Notification[];
    },
    enabled: !!user?.id,
  });
}

export function useUnreadNotificationCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["notifications-unread-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from("notifications")
        .select("*", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
  });
}

export function useNotificationPreferences() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["notification-preferences", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;

      const { data, error } = await supabase
        .from("notification_preferences")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (error) throw error;
      return data as NotificationPreferences | null;
    },
    enabled: !!user?.id,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("id", notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count", user?.id] });
    },
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) return;

      const { error } = await supabase
        .from("notifications")
        .update({ is_read: true })
        .eq("user_id", user.id)
        .eq("is_read", false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", user?.id] });
      queryClient.invalidateQueries({ queryKey: ["notifications-unread-count", user?.id] });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (preferences: Partial<Omit<NotificationPreferences, 'id' | 'user_id'>>) => {
      if (!user?.id) throw new Error("Not authenticated");

      const { data: existing } = await supabase
        .from("notification_preferences")
        .select("id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("notification_preferences")
          .update(preferences)
          .eq("user_id", user.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("notification_preferences")
          .insert({ user_id: user.id, ...preferences });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notification-preferences", user?.id] });
    },
  });
}

// Hook that monitors accuracy and triggers in-app notifications
export function useAccuracyMonitor(settings: {
  enabled: boolean;
  thresholdPercent: number;
  windowDays: number;
  minPredictions: number;
}) {
  const { data: predictions = [] } = usePredictions();
  const lastAlertRef = useRef<string | null>(null);

  useEffect(() => {
    if (!settings.enabled || predictions.length === 0) return;

    const now = new Date();
    const windowStart = new Date(now.getTime() - settings.windowDays * 24 * 60 * 60 * 1000);

    // Filter predictions within the window that have outcomes
    const recentPredictions = predictions.filter((p) => {
      const createdAt = new Date(p.created_at);
      return createdAt >= windowStart && p.was_correct !== null;
    });

    const total = recentPredictions.length;
    if (total < settings.minPredictions) return;

    const correct = recentPredictions.filter((p) => p.was_correct === true).length;
    const accuracy = (correct / total) * 100;

    // Create unique alert key to prevent duplicate toasts
    const alertKey = `${Math.floor(accuracy)}-${total}`;
    if (lastAlertRef.current === alertKey) return;

    if (accuracy < settings.thresholdPercent) {
      lastAlertRef.current = alertKey;
      toast({
        variant: "destructive",
        title: "⚠️ Accuracy Alert",
        description: `Prediction accuracy has dropped to ${accuracy.toFixed(1)}%, below your ${settings.thresholdPercent}% threshold.`,
      });
    }
  }, [predictions, settings]);
}

// Hook that monitors for high-value picks and triggers notifications
export function useHighValuePickMonitor(preferences: {
  enabled: boolean;
  minConfidence: number;
  minOdds: number;
  maxOdds: number;
}) {
  const { data: predictions = [] } = usePredictions();
  const notifiedIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!preferences.enabled || predictions.length === 0) return;

    // Find new high-value predictions
    const highValuePicks = predictions.filter((p) => {
      // Skip if already notified
      if (notifiedIdsRef.current.has(p.id)) return false;
      
      // Check if it's a pending prediction (upcoming match)
      if (p.was_correct !== null) return false;
      
      // Check if kickoff is in the future
      if (p.kickoff_time && new Date(p.kickoff_time) < new Date()) return false;
      
      // Check criteria
      return (
        p.confidence_score >= preferences.minConfidence &&
        p.odds >= preferences.minOdds &&
        p.odds <= preferences.maxOdds
      );
    });

    // Notify for each new high-value pick (limit to 3 at a time)
    highValuePicks.slice(0, 3).forEach((pick) => {
      notifiedIdsRef.current.add(pick.id);
      
      toast({
        title: "🎯 High-Value Pick Detected",
        description: `${pick.team_home} vs ${pick.team_away}: ${pick.predicted_outcome} @ ${(pick.odds != null ? Number(pick.odds).toFixed(2) : "—")} (${pick.confidence_score}% confidence)`,
      });
    });
  }, [predictions, preferences]);
}
